import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type CustomerLookupRow = {
  id: string;
  name?: string | null;
  code?: string | null;
  contact_email?: string | null;
};

type SoaPartyPickerProps = {
  value: string;
  label?: string;
  placeholder?: string;
  onSelect: (party: { id: string; name: string }) => void;
};

export function SoaPartyPicker({ value, label, placeholder, onSelect }: SoaPartyPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<CustomerLookupRow[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadOptions = useCallback(async (query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          limit: '12',
          page: '1',
          sort: 'name',
          order: 'asc',
        });
        if (query.trim()) params.set('search', query.trim());
        const res = await api.get<{ items: CustomerLookupRow[] }>(`/customers?${params}`);
        setOptions(res.items ?? []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 200);
  }, []);

  useEffect(() => {
    if (!open) return;
    void loadOptions(search);
  }, [loadOptions, open, search]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
          <span className="truncate">{label || value || placeholder || 'Select party…'}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search customer / vendor…" value={search} onValueChange={setSearch} />
          <CommandList>
            {loading ? (
              <div className="px-3 py-6 text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching parties…
              </div>
            ) : null}
            {!loading ? <CommandEmpty>No matching party found.</CommandEmpty> : null}
            <CommandGroup>
              {options.map((option) => {
                const optionLabel = option.name || option.code || option.id;
                const subLabel = [option.code, option.contact_email].filter(Boolean).join(' · ');
                return (
                  <CommandItem
                    key={option.id}
                    value={`${option.id}:${optionLabel}`}
                    onSelect={() => {
                      onSelect({ id: option.id, name: optionLabel });
                      setOpen(false);
                    }}
                    className="flex items-start justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{optionLabel}</p>
                      <p className="truncate text-xs text-muted-foreground">{subLabel || option.id}</p>
                    </div>
                    <Check className={cn('mt-0.5 h-4 w-4 shrink-0', value === option.id ? 'opacity-100' : 'opacity-0')} />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}